import { useState } from "react";
import type { AdminLogLevel } from "@/server/admin/logEntries";
import type { AdminBootstrapInfo } from "@/utils/adminAuth";
import { AdminSecretGate } from "./AdminSecretGate";
import { AdminNavSidebar, type AdminPage } from "./AdminNavSidebar";
import { LogsSection } from "./LogsSection";
import { OverviewSection } from "./OverviewSection";
import { useAdminLive } from "../hooks/useAdminLive";
import { useAdminMetrics } from "../hooks/useAdminMetrics";
import type { MetricsWindow } from "../utils/types";

type AdminDashboardProps = {
  bootstrap: AdminBootstrapInfo;
};

const isMetricsEmpty = (metrics: ReturnType<typeof useAdminMetrics>["metrics"]) => {
  if (!metrics) return true;

  const total = metrics.statusCounts.started
    + metrics.statusCounts.completed
    + metrics.statusCounts.failed
    + metrics.statusCounts.fallback;

  return total === 0 && metrics.toolCalls.total === 0;
};

export const AdminDashboard = ({ bootstrap }: AdminDashboardProps) => {
  const [page, setPage] = useState<AdminPage>("overview");
  const [window, setWindow] = useState<MetricsWindow>("1h");
  const [level, setLevel] = useState<AdminLogLevel | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [phone, setPhone] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(bootstrap.secret);

  const authReady = Boolean(clientSecret);
  const { metrics, loading: metricsLoading, error: metricsError, refresh } = useAdminMetrics(
    window,
    authReady,
  );
  const { logs, activeRuns, connected, error: liveError, reload } = useAdminLive({
    onMetricsTick: refresh,
    phone,
    enabled: authReady,
  });

  const goToLogs = (query: string) => {
    setSearch(query);
    setPage("logs");
  };

  const showSetupHint = authReady
    && !metricsError
    && !liveError
    && connected
    && logs.length === 0
    && isMetricsEmpty(metrics);

  if (!authReady) {
    return (
      <AdminSecretGate
        onSignIn={(secret) => setClientSecret(secret)}
      />
    );
  }

  return (
    <div className="admin-shell">
      <AdminNavSidebar active={page} onNavigate={setPage} />

      <div className="admin-main">

        {metricsError ? (
          <p className="admin-page-error">{metricsError}</p>
        ) : null}

        {liveError ? (
          <p className="admin-page-error">{liveError}</p>
        ) : null}

        {showSetupHint ? (
          <div className="admin-setup-hint">
            <p>No agent activity yet. Run <code>bun run agent</code> in another terminal and send a test message.</p>
            {!bootstrap.dbConfigured ? (
              <p>Set <code>DB_CONNECTION</code> in .env and run <code>bun run db:migrate</code> for overview metrics.</p>
            ) : null}
          </div>
        ) : null}

        {page === "overview" ? (
          <OverviewSection
            metrics={metrics}
            loading={metricsLoading}
            window={window}
            onWindowChange={setWindow}
            activeRuns={activeRuns}
            onSelectFailure={(runId) => goToLogs(runId.slice(0, 8))}
          />
        ) : null}

        {page === "logs" ? (
          <LogsSection
            logs={logs}
            level={level}
            onLevelChange={setLevel}
            search={search}
            onSearchChange={setSearch}
            phone={phone}
            onPhoneChange={setPhone}
            connected={connected}
            onRefresh={() => void reload()}
          />
        ) : null}
      </div>
    </div>
  );
};
